import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Badge } from './ui/badge';
import {
  BookOpen, Download, FileText, Search, ExternalLink, Star, 
  AlertTriangle, HardHat, Heart, Flame, Zap, Shield, Lock,
  Building, Wrench, Eye, ChevronDown, ChevronUp, CheckCircle,
  Loader2, Package, Link2, Bookmark, TrendingUp, Clock,
  FileDown, FolderDown, Globe, Calculator, ClipboardList
} from 'lucide-react';

const EnhancedKnowledgeCenter = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [expandedResource, setExpandedResource] = useState(null);
  const [downloading, setDownloading] = useState({});
  const [downloadingAll, setDownloadingAll] = useState(false);

  // Streamlined categories
  const categories = [
    { id: 'all', name: 'All', icon: BookOpen, color: 'bg-slate-500' },
    { id: 'fall_protection', name: 'Fall Protection', icon: AlertTriangle, color: 'bg-orange-500' },
    { id: 'ppe', name: 'PPE', icon: HardHat, color: 'bg-blue-500' },
    { id: 'electrical', name: 'Electrical', icon: Zap, color: 'bg-yellow-500' },
    { id: 'fire', name: 'Fire Safety', icon: Flame, color: 'bg-red-500' },
    { id: 'hazcom', name: 'HazCom', icon: AlertTriangle, color: 'bg-purple-500' },
    { id: 'lockout', name: 'LOTO', icon: Lock, color: 'bg-green-500' },
    { id: 'confined', name: 'Confined Space', icon: Building, color: 'bg-indigo-500' },
    { id: 'first_aid', name: 'First Aid', icon: Heart, color: 'bg-pink-500' },
  ];

  // Featured resources - most important/popular
  const featuredResources = [
    {
      id: 'featured_1',
      title: 'OSHA Top 10 Violations',
      description: 'Most frequently cited OSHA standards - know what inspectors look for',
      icon: TrendingUp,
      color: 'from-red-500 to-orange-500',
      link: 'https://www.osha.gov/top10citedstandards'
    },
    {
      id: 'featured_2', 
      title: 'Fall Protection Quick Card',
      description: 'Essential fall protection requirements at a glance',
      icon: AlertTriangle,
      color: 'from-orange-500 to-yellow-500',
      link: 'https://www.osha.gov/sites/default/files/publications/OSHA3146.pdf'
    },
    {
      id: 'featured_3',
      title: 'Safety Data Sheet Guide',
      description: 'How to read and understand SDS documents',
      icon: FileText,
      color: 'from-blue-500 to-indigo-500',
      link: 'https://www.osha.gov/sites/default/files/publications/OSHA3514.pdf'
    },
    {
      id: 'featured_4',
      title: 'PPE Selection Guide',
      description: 'Choose the right protective equipment for each hazard',
      icon: Shield,
      color: 'from-green-500 to-emerald-500',
      link: 'https://www.osha.gov/personal-protective-equipment'
    }
  ];

  // OSHA Quick Links - Direct links to official resources
  const oshaQuickLinks = [
    { title: 'OSHA Law & Regulations', url: 'https://www.osha.gov/laws-regs', icon: Globe },
    { title: 'Safety & Health Topics', url: 'https://www.osha.gov/topics', icon: BookOpen },
    { title: 'Training Resources', url: 'https://www.osha.gov/training', icon: ClipboardList },
    { title: 'Publications & Posters', url: 'https://www.osha.gov/publications', icon: FileText },
    { title: 'Recordkeeping Forms', url: 'https://www.osha.gov/recordkeeping/forms', icon: ClipboardList },
    { title: 'Hazard Recognition', url: 'https://www.osha.gov/hazard-recognition', icon: Eye },
  ];

  // Safety Tools
  const safetyTools = [
    {
      id: 'tool_1',
      title: 'Incident Rate Calculator',
      description: 'Calculate your OSHA incident rates (TRIR, DART, LTIR)',
      icon: Calculator,
      type: 'calculator',
      link: 'https://www.osha.gov/data/commonstats'
    },
    {
      id: 'tool_2',
      title: 'Heat Index Calculator',
      description: 'Determine heat stress risk levels for outdoor workers',
      icon: Flame,
      type: 'calculator',
      link: 'https://www.osha.gov/heat-exposure/calculator'
    },
    {
      id: 'tool_3',
      title: 'Noise Exposure Calculator',
      description: 'Calculate worker noise exposure and required protection',
      icon: Eye,
      type: 'calculator',
      link: 'https://www.osha.gov/noise/calculator'
    },
    {
      id: 'tool_4',
      title: 'Silica Table 1 Lookup',
      description: 'Find required controls for silica-generating tasks',
      icon: Search,
      type: 'lookup',
      link: 'https://www.osha.gov/silica-crystalline/table-1-tasks'
    },
    {
      id: 'tool_5',
      title: 'eTools & Expert Advisors',
      description: 'Interactive guidance for specific hazards and industries',
      icon: Wrench,
      type: 'tool',
      link: 'https://www.osha.gov/etools'
    },
    {
      id: 'tool_6',
      title: 'OSHA QuickTakes Newsletter',
      description: 'Subscribe to OSHA news and updates',
      icon: BookOpen,
      type: 'newsletter',
      link: 'https://www.osha.gov/quicktakes'
    }
  ];

  // Downloadable Resources - Comprehensive content
  const downloadableResources = [
    {
      id: 'dl_fall_1',
      category: 'fall_protection',
      title: 'Fall Protection Complete Guide',
      description: 'Comprehensive guide covering OSHA 1926.501 - guardrails, PFAS, safety nets, and rescue planning',
      oshaStandard: '1926.501',
      oshaUrl: 'https://www.osha.gov/laws-regs/regulations/standardnumber/1926/1926.501',
      readTime: 25,
      content: `FALL PROTECTION COMPLETE GUIDE
========================================

OSHA Standard: 29 CFR 1926.501 - Duty to Have Fall Protection

OVERVIEW
--------
Falls are the leading cause of death in construction, accounting for over 33% of all construction fatalities. This guide covers all OSHA fall protection requirements.

WHEN FALL PROTECTION IS REQUIRED
--------------------------------
• Working at heights of 6 feet or more above a lower level (construction)
• Working at heights of 4 feet or more (general industry)
• Working near unprotected sides, edges, or holes
• Leading edge work
• Excavation work near edges

TYPES OF FALL PROTECTION SYSTEMS
--------------------------------

1. GUARDRAIL SYSTEMS
   • Top rail: 42 inches (±3 inches) above walking surface
   • Mid rail: Midway between top rail and surface
   • Must withstand 200 pounds of force
   • Posts no more than 8 feet apart

2. PERSONAL FALL ARREST SYSTEMS (PFAS)
   Components:
   • Full-body harness (body belts NOT allowed)
   • Shock-absorbing lanyard or SRL
   • Anchor point (5,000 lbs. capacity minimum)
   • Connectors with locking mechanisms
   
   Requirements:
   • Limit free fall to 6 feet
   • Limit maximum arresting force to 1,800 lbs
   • Stop fall before worker contacts lower level
   • Daily inspection before each use

3. SAFETY NET SYSTEMS
   • Install as close as possible to work surface
   • Never more than 30 feet below
   • Extend 8-13 feet beyond edge depending on height
   • Weekly inspections required

4. WARNING LINE SYSTEMS (Roofing only)
   • Set up 6 feet from roof edge
   • Height: 34-39 inches
   • Flagged every 6 feet

INSPECTION CHECKLIST
--------------------
□ Check harness webbing for cuts, burns, fraying
□ Inspect stitching for broken threads
□ Examine D-rings for cracks or distortion
□ Verify buckles function properly
□ Check lanyard for damage
□ Test SRL retraction
□ Verify anchor point capacity

RESCUE PLANNING
---------------
• Must have rescue plan BEFORE work begins
• Rescue within 15-20 minutes (suspension trauma risk)
• Train workers on rescue procedures
• Have rescue equipment available

TRAINING REQUIREMENTS
---------------------
Each employee must be trained to recognize fall hazards and understand:
• Nature of fall hazards
• Correct procedures for erecting, maintaining, disassembling systems
• Proper use and operation of equipment
• Role in fall protection plan

For more information: www.osha.gov/fall-protection`
    },
    {
      id: 'dl_ppe_1',
      category: 'ppe',
      title: 'PPE Selection & Use Guide',
      description: 'Complete guide to selecting, using, and maintaining personal protective equipment',
      oshaStandard: '1910.132',
      oshaUrl: 'https://www.osha.gov/laws-regs/regulations/standardnumber/1910/1910.132',
      readTime: 20,
      content: `PERSONAL PROTECTIVE EQUIPMENT (PPE) GUIDE
==========================================

OSHA Standard: 29 CFR 1910.132 - General Requirements

PPE HIERARCHY
-------------
Remember: PPE is the LAST line of defense. First try to:
1. Eliminate the hazard
2. Substitute with less hazardous option
3. Use engineering controls
4. Use administrative controls
5. Then use PPE

HEAD PROTECTION (Hard Hats)
---------------------------
Types:
• Type I: Top impact protection
• Type II: Top and side impact protection
• Class E: Electrical protection up to 20,000 volts
• Class G: General use, up to 2,200 volts
• Class C: No electrical protection

Replace when:
• Cracked, dented, or damaged
• Exposed to chemical splash
• After any impact
• Per manufacturer's recommendation (typically 2-5 years)

EYE & FACE PROTECTION
---------------------
• Safety glasses: Impact protection
• Goggles: Chemical splash, dust protection
• Face shields: Full face protection (use with glasses)
• Welding helmets: Proper shade for welding type

Selection based on hazard:
• Flying particles → Safety glasses with side shields
• Chemical splash → Chemical goggles
• Dust → Tight-fitting goggles
• Radiant energy → Proper shade filter

HAND PROTECTION
---------------
Glove selection by hazard:
• Cuts → Cut-resistant (ANSI cut levels A1-A9)
• Chemicals → Chemical-resistant (check permeation data)
• Heat → Heat-resistant
• Electrical → Insulated electrical gloves
• Abrasion → Leather or synthetic

FOOT PROTECTION
---------------
• Steel toe: Impact protection
• Metatarsal guards: Heavy object protection
• Electrical hazard (EH): Insulated soles
• Puncture resistant: Construction, warehouse
• Slip resistant: Food service, wet areas

HEARING PROTECTION
------------------
Required when noise exceeds 85 dB TWA

Options:
• Earplugs: NRR 20-33 dB
• Earmuffs: NRR 20-30 dB
• Both combined: Additional 5 dB protection

RESPIRATORY PROTECTION
----------------------
Requires written program, medical evaluation, and fit testing

Types:
• N95: Particulates only
• Half-face APR: Gas/vapor cartridges
• Full-face APR: Higher protection factor
• PAPR: Powered air-purifying
• SCBA: Highest protection

For more information: www.osha.gov/personal-protective-equipment`
    },
    {
      id: 'dl_elec_1',
      category: 'electrical',
      title: 'Electrical Safety & NFPA 70E Guide',
      description: 'Arc flash protection, approach boundaries, and electrical safe work practices',
      oshaStandard: '1910.333',
      oshaUrl: 'https://www.osha.gov/laws-regs/regulations/standardnumber/1910/1910.333',
      readTime: 22,
      content: `ELECTRICAL SAFETY GUIDE
=======================

OSHA Standards: 29 CFR 1910.331-335 (General Industry)
                29 CFR 1926.400-449 (Construction)

ELECTRICAL HAZARDS
------------------
• Electrocution (death by electric shock)
• Electric shock
• Burns (electrical, arc flash, thermal)
• Falls caused by electrical contact

Current effects on the body:
• 1 mA: Perception threshold
• 5 mA: Maximum harmless current
• 10-20 mA: Muscle paralysis ("can't let go")
• 50 mA: Pain, possible fainting
• 100-200 mA: Ventricular fibrillation
• >200 mA: Severe burns, cardiac arrest

SAFE WORK PRACTICES
-------------------
1. De-energize equipment before working (preferred)
2. Use LOTO procedures
3. Use appropriate PPE
4. Maintain safe approach distances
5. Use insulated tools

APPROACH BOUNDARIES (NFPA 70E)
------------------------------
1. Limited Approach Boundary
   - Unqualified persons must stay outside
   - Shock hazard exists

2. Restricted Approach Boundary  
   - Only qualified persons with PPE
   - Increased shock hazard

3. Arc Flash Boundary
   - Risk of 2nd degree burns
   - Arc-rated PPE required

4. Prohibited Approach Boundary
   - Same as direct contact
   - Highest PPE level required

ARC FLASH PPE CATEGORIES
------------------------
Category 1: Min 4 cal/cm²
• Arc-rated long sleeve shirt and pants
• Safety glasses or goggles
• Hearing protection
• Leather gloves

Category 2: Min 8 cal/cm²  
• Arc-rated shirt, pants, and face shield
• Arc-rated balaclava or hood
• Safety glasses
• Hearing protection
• Leather gloves

Category 3: Min 25 cal/cm²
• Arc flash suit (hood, jacket, pants)
• Arc-rated gloves and leather protectors
• Safety glasses
• Hearing protection

Category 4: Min 40 cal/cm²
• Multi-layer arc flash suit
• Arc-rated gloves and leather protectors
• Hard hat with arc-rated face shield
• Hearing protection

WORKING NEAR POWER LINES
------------------------
Minimum clearance distances:
• Up to 50kV: 10 feet
• 50-200kV: 15 feet
• 200-350kV: 20 feet
• 350-500kV: 25 feet
• 500-750kV: 35 feet

For more information: www.osha.gov/electrical`
    },
    {
      id: 'dl_hazcom_1',
      category: 'hazcom',
      title: 'Hazard Communication (GHS) Guide',
      description: 'Understanding SDS, labels, pictograms, and the Globally Harmonized System',
      oshaStandard: '1910.1200',
      oshaUrl: 'https://www.osha.gov/laws-regs/regulations/standardnumber/1910/1910.1200',
      readTime: 18,
      content: `HAZARD COMMUNICATION (HAZCOM) GUIDE
====================================

OSHA Standard: 29 CFR 1910.1200

THE GLOBALLY HARMONIZED SYSTEM (GHS)
------------------------------------
GHS provides a standardized approach to:
• Classification of chemical hazards
• Labels with consistent information
• Safety Data Sheets in standard format

EMPLOYER REQUIREMENTS
---------------------
1. Develop written HazCom program
2. Maintain SDS for all hazardous chemicals
3. Ensure proper labeling
4. Train employees

GHS PICTOGRAMS (9 Total)
------------------------
1. FLAME - Flammables, pyrophorics, self-heating
2. FLAME OVER CIRCLE - Oxidizers
3. EXPLODING BOMB - Explosives, self-reactives
4. CORROSION - Skin corrosion, eye damage
5. GAS CYLINDER - Compressed gases
6. SKULL & CROSSBONES - Acute toxicity (fatal/toxic)
7. EXCLAMATION MARK - Irritant, narcotic effects
8. HEALTH HAZARD - Carcinogen, mutagenicity
9. ENVIRONMENT - Aquatic toxicity (optional)

LABEL REQUIREMENTS
------------------
Required elements:
• Product identifier
• Signal word ("Danger" or "Warning")
• Hazard statement(s)
• Pictogram(s)
• Precautionary statement(s)
• Supplier information

SAFETY DATA SHEET (SDS) - 16 SECTIONS
-------------------------------------
Section 1: Identification
Section 2: Hazard(s) identification
Section 3: Composition/ingredients
Section 4: First-aid measures
Section 5: Fire-fighting measures
Section 6: Accidental release measures
Section 7: Handling and storage
Section 8: Exposure controls/PPE
Section 9: Physical/chemical properties
Section 10: Stability and reactivity
Section 11: Toxicological information
Section 12: Ecological information*
Section 13: Disposal considerations*
Section 14: Transport information*
Section 15: Regulatory information*
Section 16: Other information

*Not enforced by OSHA but required by GHS

KEY SECTIONS FOR WORKERS
------------------------
Sections 1-8 contain the most critical safety information:
• What is it? (Section 1)
• What are the hazards? (Section 2)
• What's in it? (Section 3)
• First aid? (Section 4)
• Fire response? (Section 5)
• Spill cleanup? (Section 6)
• How to handle safely? (Section 7)
• What PPE to use? (Section 8)

TRAINING REQUIREMENTS
---------------------
Employees must understand:
• Location and availability of written program and SDSs
• Physical and health hazards of chemicals
• Protective measures and procedures
• How to read labels and SDSs

For more information: www.osha.gov/hazcom`
    },
    {
      id: 'dl_loto_1',
      category: 'lockout',
      title: 'Lockout/Tagout (LOTO) Procedures',
      description: 'Energy control procedures for servicing and maintenance of equipment',
      oshaStandard: '1910.147',
      oshaUrl: 'https://www.osha.gov/laws-regs/regulations/standardnumber/1910/1910.147',
      readTime: 20,
      content: `LOCKOUT/TAGOUT (LOTO) GUIDE
===========================

OSHA Standard: 29 CFR 1910.147

PURPOSE
-------
Control hazardous energy during servicing and maintenance to prevent:
• Unexpected startup
• Release of stored energy
• Approximately 50,000 injuries and 120 deaths annually

TYPES OF HAZARDOUS ENERGY
-------------------------
1. Electrical - most common
2. Mechanical - rotating parts, gears
3. Hydraulic - pressurized fluids
4. Pneumatic - compressed air
5. Chemical - reactive substances
6. Thermal - steam, hot surfaces
7. Gravitational - suspended loads

EMPLOYEE CLASSIFICATIONS
------------------------
AUTHORIZED Employee:
• Locks out machines/equipment
• Performs servicing or maintenance
• Receives detailed LOTO training

AFFECTED Employee:
• Operates equipment being serviced
• Works in area where LOTO is used
• Must be instructed on LOTO purpose

OTHER Employees:
• Work in areas where LOTO may be used
• Must not attempt to restart equipment

SIX-STEP LOCKOUT PROCEDURE
--------------------------
1. PREPARATION
   • Identify all energy sources
   • Identify isolation devices
   • Notify affected employees

2. SHUTDOWN
   • Use normal shutdown procedures
   • Operate controls to safe state

3. ISOLATION
   • Locate all energy isolating devices
   • Isolate from all energy sources

4. LOCKOUT/TAGOUT APPLICATION
   • Apply locks and tags
   • Each authorized employee applies own lock
   • Tags must identify employee

5. STORED ENERGY RELEASE
   • Release/restrain stored energy
   • Block, bleed, or discharge
   • Verify zero energy state

6. VERIFICATION
   • Attempt to start equipment
   • Verify isolation is effective
   • Return controls to safe position

REMOVING LOTO DEVICES
---------------------
1. Verify work is complete
2. Remove all tools and materials
3. Ensure employees are clear
4. Notify affected employees
5. Remove locks/tags (only by person who applied)
6. Re-energize equipment

PERIODIC INSPECTION
-------------------
Required at least annually:
• Review procedures with authorized employees
• Correct any deviations
• Document inspection

GROUP LOCKOUT/TAGOUT
--------------------
When multiple employees service equipment:
• Single authorized employee coordinates
• Each worker applies personal lock to group lockbox
• Lockbox controls primary isolating devices

For more information: www.osha.gov/lockout-tagout`
    },
    {
      id: 'dl_confined_1',
      category: 'confined',
      title: 'Confined Space Entry Guide',
      description: 'Permit-required confined space procedures, atmospheric testing, and rescue planning',
      oshaStandard: '1910.146',
      oshaUrl: 'https://www.osha.gov/laws-regs/regulations/standardnumber/1910/1910.146',
      readTime: 22,
      content: `CONFINED SPACE ENTRY GUIDE
==========================

OSHA Standard: 29 CFR 1910.146

WHAT IS A CONFINED SPACE?
-------------------------
ALL THREE criteria must be met:
1. Large enough to bodily enter
2. Limited or restricted entry/exit
3. NOT designed for continuous occupancy

Examples: Tanks, vessels, silos, storage bins, hoppers, vaults, pits, manholes, tunnels, ducts, pipelines

PERMIT-REQUIRED CONFINED SPACE
------------------------------
A confined space with ONE OR MORE of these hazards:
• Hazardous atmosphere
• Engulfment potential
• Configuration that could trap or asphyxiate
• Any other recognized serious hazard

ATMOSPHERIC HAZARDS
-------------------
Test in this order: O2, Combustibles, Toxics

OXYGEN LEVELS:
• <19.5% - Oxygen deficient (DANGER)
• 19.5-23.5% - Normal/safe range
• >23.5% - Oxygen enriched (fire risk)

COMBUSTIBLE GASES:
• >10% LEL - Entry prohibited
• Continuous monitoring required

TOXIC SUBSTANCES:
• Must be below PEL/TLV
• Common: H2S, CO, SO2

TEST AT MULTIPLE LEVELS
- Gases stratify by density
- Test top, middle, bottom of space

ROLES AND RESPONSIBILITIES
--------------------------
ENTRY SUPERVISOR:
• Authorizes entry
• Verifies all precautions taken
• Terminates entry if necessary

AUTHORIZED ENTRANT:
• Knows hazards and symptoms
• Uses equipment properly
• Communicates with attendant
• Exits when ordered or if hazard detected

ATTENDANT:
• Monitors entrants
• Maintains communication
• Orders evacuation if needed
• Summons rescue if required
• NEVER ENTERS THE SPACE

RESCUE TEAM:
• Trained in rescue procedures
• Practices rescue annually
• Has proper equipment

ENTRY PERMIT REQUIREMENTS
-------------------------
Must include:
1. Space identification
2. Purpose of entry
3. Date and duration
4. Authorized entrants
5. Attendant(s) name
6. Entry supervisor signature
7. Hazards of the space
8. Measures to control hazards
9. Acceptable entry conditions
10. Test results
11. Rescue procedures
12. Communication methods
13. Required equipment
14. Other permits (hot work, etc.)

RESCUE PLANNING
---------------
Before ANY entry:
• Non-entry rescue preferred (retrieval system)
• If entry rescue needed, team must be trained
• Practice rescue from representative spaces
• Ensure rescue within 4 minutes (ideal)

For more information: www.osha.gov/confined-spaces`
    },
    {
      id: 'dl_fire_1',
      category: 'fire',
      title: 'Fire Safety & Prevention Guide',
      description: 'Fire extinguisher use, emergency evacuation, and hot work safety',
      oshaStandard: '1910.157',
      oshaUrl: 'https://www.osha.gov/laws-regs/regulations/standardnumber/1910/1910.157',
      readTime: 15,
      content: `FIRE SAFETY & PREVENTION GUIDE
==============================

OSHA Standard: 29 CFR 1910.157 (Portable Fire Extinguishers)

FIRE TRIANGLE
-------------
Fire requires ALL THREE:
1. FUEL - Something to burn
2. HEAT - Ignition source
3. OXYGEN - Air to support combustion

Remove any one element to extinguish fire.

FIRE CLASSES
------------
CLASS A: Ordinary combustibles
- Paper, wood, cloth, rubber, plastics
- Extinguisher: Water, foam, dry chemical

CLASS B: Flammable liquids/gases
- Gasoline, oil, grease, solvents
- Extinguisher: CO2, dry chemical, foam

CLASS C: Electrical equipment
- Energized electrical equipment
- Extinguisher: CO2, dry chemical (non-conductive)

CLASS D: Combustible metals
- Magnesium, titanium, sodium
- Extinguisher: Special dry powder (Class D)

CLASS K: Cooking oils/fats
- Commercial kitchen fires
- Extinguisher: Wet chemical

PASS METHOD - Using Fire Extinguisher
-------------------------------------
P - PULL the pin
A - AIM at base of fire
S - SQUEEZE the handle
S - SWEEP side to side

IMPORTANT CONSIDERATIONS:
• Know location of extinguishers
• Check monthly for accessibility
• Annual maintenance required
• Stand 6-8 feet from fire
• Have escape route behind you
• If fire doesn't reduce quickly, EVACUATE

EMERGENCY EVACUATION
--------------------
When alarm sounds:
1. Stop work immediately
2. Leave by nearest safe exit
3. Do NOT use elevators
4. Close doors behind you
5. Go to assembly area
6. Report to supervisor
7. Do NOT re-enter until authorized

EVACUATION PLANNING:
• Know at least 2 exit routes
• Keep exits clear at all times
• Practice evacuation drills
• Know location of assembly area
• Account for all personnel

HOT WORK SAFETY
---------------
Hot work = welding, cutting, brazing, soldering

PERMIT REQUIRED when:
• Within 35 feet of combustibles
• In confined spaces
• Near hazardous atmospheres

PRECAUTIONS:
• Fire watch during and 30 min after
• Remove or protect combustibles
• Cover floor openings
• Have fire extinguisher ready
• Check for hazardous atmospheres

FIRE PREVENTION TIPS
--------------------
• Maintain good housekeeping
• Store flammables properly
• Don't overload electrical circuits
• Keep heat sources away from combustibles
• Inspect wiring regularly
• No smoking near flammables
• Maintain fire suppression systems

For more information: www.osha.gov/fire-safety`
    },
    {
      id: 'dl_first_aid_1',
      category: 'first_aid',
      title: 'Workplace First Aid Essentials',
      description: 'Emergency response, CPR basics, bleeding control, and when to call 911',
      oshaStandard: '1910.151',
      oshaUrl: 'https://www.osha.gov/laws-regs/regulations/standardnumber/1910/1910.151',
      readTime: 18,
      content: `WORKPLACE FIRST AID GUIDE
=========================

OSHA Standard: 29 CFR 1910.151

WHEN TO CALL 911
----------------
Call immediately for:
• Unconscious person
• Difficulty breathing
• Chest pain
• Severe bleeding
• Seizures
• Severe burns
• Poisoning/overdose
• Severe allergic reaction
• Broken bones (compound fractures)
• Electric shock
• Heat stroke

EMERGENCY RESPONSE STEPS
------------------------
1. Assess the scene (is it safe?)
2. Call for help (911 or site emergency)
3. Check responsiveness
4. Provide care within your training
5. Stay with victim until help arrives

CPR BASICS (Hands-Only for Untrained)
-------------------------------------
If person is unresponsive and not breathing normally:

1. Call 911 (or have someone call)
2. Push hard and fast on center of chest
   - Rate: 100-120 compressions per minute
   - Depth: At least 2 inches
3. Continue until help arrives or AED available

WITH AED:
1. Turn on AED
2. Attach pads as shown
3. Follow voice prompts
4. Clear before shock
5. Resume CPR after shock

BLEEDING CONTROL
----------------
1. Apply direct pressure with clean cloth
2. Maintain pressure (don't remove cloth)
3. If soaks through, add more on top
4. Elevate injured area if possible
5. Apply tourniquet for life-threatening limb bleeding

SEVERE BLEEDING - STOP THE BLEED:
• Direct pressure first
• Pack wound if deep
• Tourniquet 2-3 inches above wound
• Note time applied
• Don't remove tourniquet

BURNS
-----
MINOR BURNS (1st degree):
• Cool with running water 10-20 minutes
• Cover with sterile bandage
• Don't apply ice or butter

SERIOUS BURNS (2nd/3rd degree):
• Call 911
• Don't remove stuck clothing
• Cover with clean, dry cloth
• Treat for shock

CHEMICAL BURNS:
• Flush with water 20+ minutes
• Remove contaminated clothing
• Check SDS for specific guidance

SHOCK
-----
Signs: Pale skin, rapid breathing, weakness, confusion

Treatment:
• Call 911
• Lay person flat, elevate legs 12 inches
• Keep warm with blanket
• Don't give food or drink
• Monitor breathing

HEAT-RELATED ILLNESS
--------------------
HEAT EXHAUSTION:
Signs: Heavy sweating, weakness, nausea, headache
Treatment: Move to cool area, water, cool cloths

HEAT STROKE (EMERGENCY):
Signs: High temp, hot/dry skin, confusion, unconscious
Treatment: Call 911, cool rapidly, don't give fluids

FIRST AID KIT REQUIREMENTS
--------------------------
Minimum supplies per ANSI Z308.1:
• Adhesive bandages
• Adhesive tape
• Antibiotic treatment
• Antiseptic
• Bandage compress
• Breathing barrier
• Burn dressing
• Burn treatment
• Cold pack
• Eye covering
• Eye wash
• First aid guide
• Gloves
• Roller bandage
• Scissors
• Sterile pad
• Tourniquet
• Triangular bandage

For more information: www.osha.gov/medical-first-aid`
    }
  ];

  // Filter resources based on search and category
  const filteredResources = useMemo(() => {
    return downloadableResources.filter(resource => {
      const matchesCategory = selectedCategory === 'all' || resource.category === selectedCategory;
      const matchesSearch = searchQuery === '' || 
        resource.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        resource.description.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesCategory && matchesSearch;
    });
  }, [selectedCategory, searchQuery]);

  // Download single resource
  const downloadResource = async (resource) => {
    try {
      setDownloading(prev => ({ ...prev, [resource.id]: true }));
      
      const content = `${resource.title}\n${'='.repeat(resource.title.length)}\n\n${resource.description}\n\nOSHA Standard: ${resource.oshaStandard}\nReference: ${resource.oshaUrl}\n\n${'-'.repeat(50)}\n\n${resource.content}`;
      
      const blob = new Blob([content], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${resource.title.replace(/[^a-z0-9]/gi, '_')}.txt`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      setTimeout(() => {
        setDownloading(prev => ({ ...prev, [resource.id]: false }));
      }, 1500);
    } catch (error) {
      console.error('Download failed:', error);
      setDownloading(prev => ({ ...prev, [resource.id]: false }));
    }
  };

  // Download all filtered resources
  const downloadAll = async () => {
    setDownloadingAll(true);
    
    // Combine all resources into one file
    let combinedContent = `OSHA SAFETY RESOURCES COMPILATION
${'='.repeat(50)}
Generated: ${new Date().toLocaleDateString()}
Total Resources: ${filteredResources.length}

TABLE OF CONTENTS
${'-'.repeat(30)}
${filteredResources.map((r, i) => `${i + 1}. ${r.title}`).join('\n')}

${'='.repeat(50)}

`;

    filteredResources.forEach((resource, index) => {
      combinedContent += `\n\n${'#'.repeat(50)}\n`;
      combinedContent += `RESOURCE ${index + 1}: ${resource.title}\n`;
      combinedContent += `${'#'.repeat(50)}\n\n`;
      combinedContent += `OSHA Standard: ${resource.oshaStandard}\n`;
      combinedContent += `Reference: ${resource.oshaUrl}\n\n`;
      combinedContent += resource.content;
      combinedContent += `\n\n`;
    });

    const blob = new Blob([combinedContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `OSHA_Safety_Resources_${selectedCategory === 'all' ? 'Complete' : selectedCategory}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    setTimeout(() => setDownloadingAll(false), 2000);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4 md:p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        
        {/* Header */}
        <div className="relative rounded-2xl overflow-hidden shadow-xl">
          <div className="absolute inset-0 opacity-20">
            <img 
              src="https://images.unsplash.com/photo-1497633762265-9d179a990aa6?auto=format&fit=crop&w=1200&q=80" 
              alt="Safety knowledge library books"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-r from-indigo-600 via-blue-600 to-indigo-700" />
          <div className="relative px-6 md:px-8 py-8 md:py-10">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                <BookOpen className="w-6 h-6 text-white" />
              </div>
              <Badge className="bg-white/20 text-white border-white/30">
                <Star className="w-3 h-3 mr-1" />
                OSHA Compliant
              </Badge>
            </div>
            <h1 className="text-2xl md:text-4xl font-bold text-white mb-2">
              Safety Knowledge Center
            </h1>
            <p className="text-indigo-100 text-sm md:text-base max-w-2xl">
              Comprehensive OSHA safety resources, tools, and downloadable guides to enhance workplace safety
            </p>
          </div>
        </div>

        {/* Featured Resources */}
        <div className="space-y-3">
          <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
            <Star className="w-5 h-5 text-yellow-500" />
            Featured Resources
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {featuredResources.map((resource) => {
              const Icon = resource.icon;
              return (
                <a
                  key={resource.id}
                  href={resource.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group"
                >
                  <Card className="h-full hover:shadow-lg transition-all duration-300 border-0 overflow-hidden">
                    <div className={`h-2 bg-gradient-to-r ${resource.color}`} />
                    <CardContent className="p-4">
                      <div className={`w-10 h-10 rounded-lg bg-gradient-to-r ${resource.color} flex items-center justify-center mb-3`}>
                        <Icon className="w-5 h-5 text-white" />
                      </div>
                      <h3 className="font-semibold text-slate-900 mb-1 group-hover:text-blue-600 transition-colors">
                        {resource.title}
                      </h3>
                      <p className="text-sm text-slate-600 line-clamp-2">{resource.description}</p>
                      <div className="flex items-center gap-1 mt-3 text-xs text-blue-600">
                        <ExternalLink className="w-3 h-3" />
                        View Resource
                      </div>
                    </CardContent>
                  </Card>
                </a>
              );
            })}
          </div>
        </div>

        {/* Main Tabs */}
        <Tabs defaultValue="resources" className="space-y-4">
          <TabsList className="grid w-full grid-cols-4 h-auto p-1.5 gap-1 bg-white shadow-sm rounded-xl">
            <TabsTrigger 
              value="resources" 
              title="Resources"
              className="flex flex-col items-center gap-1 py-2.5 sm:py-3 px-1 sm:px-2 rounded-lg transition-all data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=active]:shadow-md"
            >
              <FileText className="w-5 h-5 sm:w-6 sm:h-6" />
              <span className="text-[10px] sm:text-xs font-medium">Resources</span>
            </TabsTrigger>
            <TabsTrigger 
              value="osha" 
              title="OSHA Links"
              className="flex flex-col items-center gap-1 py-2.5 sm:py-3 px-1 sm:px-2 rounded-lg transition-all data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=active]:shadow-md"
            >
              <Globe className="w-5 h-5 sm:w-6 sm:h-6" />
              <span className="text-[10px] sm:text-xs font-medium">OSHA</span>
            </TabsTrigger>
            <TabsTrigger 
              value="tools" 
              title="Safety Tools"
              className="flex flex-col items-center gap-1 py-2.5 sm:py-3 px-1 sm:px-2 rounded-lg transition-all data-[state=active]:bg-green-600 data-[state=active]:text-white data-[state=active]:shadow-md"
            >
              <Wrench className="w-5 h-5 sm:w-6 sm:h-6" />
              <span className="text-[10px] sm:text-xs font-medium">Tools</span>
            </TabsTrigger>
            <TabsTrigger 
              value="downloads" 
              title="Downloads"
              className="flex flex-col items-center gap-1 py-2.5 sm:py-3 px-1 sm:px-2 rounded-lg transition-all data-[state=active]:bg-purple-600 data-[state=active]:text-white data-[state=active]:shadow-md"
            >
              <Download className="w-5 h-5 sm:w-6 sm:h-6" />
              <span className="text-[10px] sm:text-xs font-medium">Downloads</span>
            </TabsTrigger>
          </TabsList>

          {/* Resources Tab */}
          <TabsContent value="resources" className="space-y-4">
            {/* Search and Filter Bar */}
            <Card>
              <CardContent className="p-4">
                <div className="flex flex-col md:flex-row gap-4">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      placeholder="Search resources..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  <Button 
                    onClick={downloadAll}
                    disabled={downloadingAll || filteredResources.length === 0}
                    className="bg-gradient-to-r from-blue-600 to-indigo-600"
                  >
                    {downloadingAll ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Downloading...</>
                    ) : (
                      <><FolderDown className="w-4 h-4 mr-2" /> Download All ({filteredResources.length})</>
                    )}
                  </Button>
                </div>
                
                {/* Category Pills */}
                <div className="flex flex-wrap gap-2 mt-4">
                  {categories.map((cat) => {
                    const Icon = cat.icon;
                    const isActive = selectedCategory === cat.id;
                    const count = cat.id === 'all' 
                      ? downloadableResources.length 
                      : downloadableResources.filter(r => r.category === cat.id).length;
                    
                    return (
                      <button
                        key={cat.id}
                        onClick={() => setSelectedCategory(cat.id)}
                        className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                          isActive 
                            ? 'bg-blue-600 text-white shadow-md' 
                            : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                        {cat.name}
                        <span className={`text-xs ${isActive ? 'text-blue-200' : 'text-slate-500'}`}>
                          ({count})
                        </span>
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Resource Cards */}
            <div className="space-y-4">
              {filteredResources.map((resource) => {
                const isExpanded = expandedResource === resource.id;
                const catInfo = categories.find(c => c.id === resource.category);
                const CatIcon = catInfo?.icon || BookOpen;
                
                return (
                  <Card key={resource.id} className="overflow-hidden hover:shadow-md transition-shadow">
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <div className={`w-8 h-8 rounded-lg ${catInfo?.color || 'bg-slate-500'} flex items-center justify-center`}>
                              <CatIcon className="w-4 h-4 text-white" />
                            </div>
                            <Badge variant="outline" className="text-xs">
                              OSHA {resource.oshaStandard}
                            </Badge>
                            <span className="text-xs text-slate-500 flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {resource.readTime} min read
                            </span>
                          </div>
                          <CardTitle className="text-lg">{resource.title}</CardTitle>
                          <CardDescription className="mt-1">{resource.description}</CardDescription>
                        </div>
                        <div className="flex flex-col gap-2">
                          <Button
                            size="sm"
                            onClick={() => downloadResource(resource)}
                            disabled={downloading[resource.id]}
                          >
                            {downloading[resource.id] ? (
                              <><CheckCircle className="w-4 h-4 mr-1 text-green-500" /> Done</>
                            ) : (
                              <><FileDown className="w-4 h-4 mr-1" /> Download</>
                            )}
                          </Button>
                          <a
                            href={resource.oshaUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center justify-center px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 transition-colors"
                          >
                            <ExternalLink className="w-3 h-3 mr-1" />
                            OSHA.gov
                          </a>
                        </div>
                      </div>
                    </CardHeader>
                    
                    <CardContent className="pt-0">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setExpandedResource(isExpanded ? null : resource.id)}
                        className="w-full justify-between text-slate-600"
                      >
                        <span className="flex items-center gap-2">
                          <Eye className="w-4 h-4" />
                          {isExpanded ? 'Hide Preview' : 'Show Preview'}
                        </span>
                        {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </Button>
                      
                      {isExpanded && (
                        <div className="mt-4 p-4 bg-slate-50 rounded-lg">
                          <pre className="whitespace-pre-wrap text-sm text-slate-700 font-mono max-h-96 overflow-y-auto">
                            {resource.content}
                          </pre>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
              
              {filteredResources.length === 0 && (
                <Card>
                  <CardContent className="py-12 text-center">
                    <Search className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                    <h3 className="text-lg font-medium text-slate-900 mb-1">No resources found</h3>
                    <p className="text-slate-600">Try adjusting your search or category filter</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* OSHA Links Tab */}
          <TabsContent value="osha" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Globe className="w-5 h-5 text-blue-600" />
                  Official OSHA Resources
                </CardTitle>
                <CardDescription>
                  Direct links to official OSHA.gov resources and tools
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {oshaQuickLinks.map((link, idx) => {
                    const Icon = link.icon;
                    return (
                      <a
                        key={idx}
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 p-4 rounded-lg border border-slate-200 hover:border-blue-300 hover:bg-blue-50 transition-all group"
                      >
                        <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                          <Icon className="w-5 h-5 text-blue-600" />
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-slate-900 group-hover:text-blue-700">{link.title}</p>
                          <p className="text-xs text-slate-500">osha.gov</p>
                        </div>
                        <ExternalLink className="w-4 h-4 text-slate-400 group-hover:text-blue-600" />
                      </a>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Popular OSHA Standards Quick Links</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {[
                    { standard: '1910.132', title: 'Personal Protective Equipment', url: 'https://www.osha.gov/laws-regs/regulations/standardnumber/1910/1910.132' },
                    { standard: '1926.501', title: 'Fall Protection', url: 'https://www.osha.gov/laws-regs/regulations/standardnumber/1926/1926.501' },
                    { standard: '1910.147', title: 'Lockout/Tagout', url: 'https://www.osha.gov/laws-regs/regulations/standardnumber/1910/1910.147' },
                    { standard: '1910.1200', title: 'Hazard Communication', url: 'https://www.osha.gov/laws-regs/regulations/standardnumber/1910/1910.1200' },
                    { standard: '1910.146', title: 'Confined Spaces', url: 'https://www.osha.gov/laws-regs/regulations/standardnumber/1910/1910.146' },
                    { standard: '1910.134', title: 'Respiratory Protection', url: 'https://www.osha.gov/laws-regs/regulations/standardnumber/1910/1910.134' },
                    { standard: '1926.1153', title: 'Silica (Construction)', url: 'https://www.osha.gov/laws-regs/regulations/standardnumber/1926/1926.1153' },
                    { standard: '1910.95', title: 'Occupational Noise', url: 'https://www.osha.gov/laws-regs/regulations/standardnumber/1910/1910.95' },
                  ].map((item, idx) => (
                    <a
                      key={idx}
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-between p-3 rounded-lg border hover:border-blue-300 hover:bg-blue-50 transition-all"
                    >
                      <div>
                        <Badge variant="outline" className="mb-1">{item.standard}</Badge>
                        <p className="text-sm font-medium text-slate-900">{item.title}</p>
                      </div>
                      <ExternalLink className="w-4 h-4 text-slate-400" />
                    </a>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tools Tab */}
          <TabsContent value="tools" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Wrench className="w-5 h-5 text-green-600" />
                  Safety Calculators & Tools
                </CardTitle>
                <CardDescription>
                  Interactive tools to help with safety calculations and compliance
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {safetyTools.map((tool) => {
                    const Icon = tool.icon;
                    return (
                      <a
                        key={tool.id}
                        href={tool.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group"
                      >
                        <Card className="h-full hover:shadow-lg transition-all border-2 hover:border-green-300">
                          <CardContent className="p-4">
                            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center mb-3">
                              <Icon className="w-6 h-6 text-white" />
                            </div>
                            <h3 className="font-semibold text-slate-900 mb-1 group-hover:text-green-700">
                              {tool.title}
                            </h3>
                            <p className="text-sm text-slate-600 mb-3">{tool.description}</p>
                            <Badge variant="secondary" className="capitalize">
                              {tool.type}
                            </Badge>
                          </CardContent>
                        </Card>
                      </a>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Downloads Tab */}
          <TabsContent value="downloads" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Package className="w-5 h-5 text-purple-600" />
                      Downloadable Resource Packs
                    </CardTitle>
                    <CardDescription>
                      Download comprehensive safety guides for offline access
                    </CardDescription>
                  </div>
                  <Button 
                    onClick={downloadAll}
                    disabled={downloadingAll}
                    size="lg"
                    className="bg-gradient-to-r from-purple-600 to-indigo-600"
                  >
                    {downloadingAll ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Downloading...</>
                    ) : (
                      <><FolderDown className="w-4 h-4 mr-2" /> Download All Resources</>
                    )}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {categories.filter(c => c.id !== 'all').map((cat) => {
                    const Icon = cat.icon;
                    const catResources = downloadableResources.filter(r => r.category === cat.id);
                    
                    return (
                      <Card key={cat.id} className="overflow-hidden">
                        <div className={`h-2 ${cat.color}`} />
                        <CardContent className="p-4">
                          <div className="flex items-center gap-3 mb-3">
                            <div className={`w-10 h-10 rounded-lg ${cat.color} flex items-center justify-center`}>
                              <Icon className="w-5 h-5 text-white" />
                            </div>
                            <div>
                              <h3 className="font-semibold text-slate-900">{cat.name}</h3>
                              <p className="text-xs text-slate-500">{catResources.length} resources</p>
                            </div>
                          </div>
                          <ul className="space-y-1 mb-4">
                            {catResources.slice(0, 3).map(r => (
                              <li key={r.id} className="text-sm text-slate-600 truncate">
                                • {r.title}
                              </li>
                            ))}
                            {catResources.length > 3 && (
                              <li className="text-sm text-slate-400">
                                +{catResources.length - 3} more
                              </li>
                            )}
                          </ul>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="w-full"
                            onClick={() => {
                              setSelectedCategory(cat.id);
                              downloadAll();
                            }}
                          >
                            <FileDown className="w-4 h-4 mr-2" />
                            Download Pack
                          </Button>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default EnhancedKnowledgeCenter;
